import React from 'react';
import Styles from './index.less';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import i18n from '@i18next/i18n';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { Application } from '@tencent/alloy-renderer';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';
import { IEditorServices } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-service.interface';
import { ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { isShowTextFill } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/link';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { DisplayRule } from '@tencent/alloy-renderer-adapter';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { replaceSrgbColorTDColor } from '@src/framework/editor/src/js/utils/color';
import _cloneDeep from 'lodash/cloneDeep';
import { TextFastStyleCache } from './text-fast-style-cache';

export interface IShapeFastStyleCardProps {
    onClick: (style) => void;
    text: string;
    textStyle: ITextRunStyle;
    slideModel: ISlideModel;
    editorServices: IEditorServices;
}

const DefaultDisplayOptions = {
    shadow: DisplayRule.Both,
    glow: DisplayRule.Both,
    softEdge: DisplayRule.Both,
    reflection: DisplayRule.Both,
    image: DisplayRule.Both,
};
const ICON_SIZE = 160;
const FONT_SIZE = 60;
const textRect = {
    left: 0,
    top: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
};

export default class TextFastStyleCard extends React.PureComponent<IShapeFastStyleCardProps, any> {
    private application: Application;
    private textStyle: ITextRunStyle;
    private cacheKey;
    private iconCache: TextFastStyleCache;

    public constructor(props) {
        super(props);
        this.iconCache = TextFastStyleCache.getInstance();
        const textStyle = replaceSrgbColorTDColor(_cloneDeep(this.props.textStyle), this.props.slideModel);
        this.textStyle = textStyle;
        this.cacheKey = Object.values(this.props.slideModel.getRelationThemeModel().clrScheme).join('') + this.props.text;
        const cache = this.iconCache.getFromCache(this.cacheKey);
        if (cache) {
            this.state = {
                url: cache.blobUrl,
            };
        } else {
            this.state = {
                url: '',
            };
            this.generateIcon();
        }
    }

    public render() {
        const { text } = this.props;
        return (
            <Tooltip title={text} trigger="hover" placement={'top'} overlayHoverable={false}>
                <div
                    className={Styles['text-fast-style-card']}
                    onClick={this.handleClick}
                    style={{ backgroundImage: `url(${this.state.url})` }}
                >
                </div>
            </Tooltip>
        );
    }

    private generateIcon() {
        const canvasElement = document.createElement('canvas');
        this.application = new Application(new BrowserTarget(), { native: canvasElement, interactive: false });
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.application.width = ICON_SIZE;
        this.application.height = ICON_SIZE;
        const paragraphList: IParagraph[] = [{
            paragraphProps: {
                lang: 'ea',
                textAlign: 'center',
            },
            textRuns: [
                {
                    styles: {
                        lang: 'latin',
                        fontSize: FONT_SIZE,
                        fontWeight: 'bold',
                        ...this.textStyle,
                    },
                    text: i18n.t('A'),
                    type: 'run',
                },
            ],
        }];
        const editor = new TextEditor({
            textBodyStyle: {
                wrapperStyle: {
                    alignItems: 'center',
                },
            },
            paragraphList,
            selectable: false,
            editable: false,
            textRect,
            getRenderTextExtraOption: this.getRenderTextExtraOption,
        }, this.props.editorServices);
        this.application.stage.addChild(editor.editorContainer);
        this.application.render();
        canvasElement.toBlob((blob) => {
            if (!blob) return;
            const blobUrl = URL.createObjectURL(blob);
            this.application.stage.removeChild(editor.editorContainer);
            this.setState({ url: blobUrl });
            this.iconCache.addToCache(this.cacheKey, { blobUrl });
        });
    }

    private getRenderTextExtraOption = option => ({
        forbidWebgl: FORBID_WEBGL_FILTER,
        effectScale: 1 / CANVAS_PT_SCALE,
        shouldShowTextFill: isShowTextFill(option.textElement),
        textDirection: TextDirectionType.HORIZONTAL,
        slideKey: '',
        filterDisplayOptions: DefaultDisplayOptions,
        forbidFilter: false,
    });

    private handleClick = () => {
        this.props.onClick(this.textStyle);
    };
}
